import React, { useState, useCallback, Dispatch, SetStateAction } from 'react';
import { LocationData, GameNotification } from '../types';
import { NAWAS_TIERS } from '../constants';

export interface RandomEvent {
  id: string;
  name: string;
  description: string;
  effect: (location: LocationData, locationDailyRevenue: number) => { revenueloss?: number; revenueboost?: number };
  probability: number; // per-day roll (checked once per 1440 ticks)
  duration: number;    // ticks the event remains active
  positive?: boolean;
  utilizationDelta?: number; // applied to contracted servers on start; reverted on end
}

export interface ActiveEvent extends RandomEvent {
  locationId: string;
  remainingDuration: number;
  startTime: number;
  // IDs of servers that were forced to 'failed' by this event — restored when event ends
  affectedServerIds?: string[];
}

// All events now work through real game mechanics.
// Revenue modifiers are removed — impacts flow through server failures and connectivity.
export const RANDOM_EVENTS: RandomEvent[] = [
  // ── Negative ────────────────────────────────────────────────────────────────
  {
    id: 'powerOutage',
    name: 'Power Outage',
    description: 'Grid failure — all servers at this location are offline until power is restored.',
    effect: () => ({}), // revenue drops because servers are failed → operationalCount = 0
    probability: 0.03,
    duration: 90, // ~22 real seconds at 1× — enough to notice, not game-breaking
  },
  {
    id: 'coolingFailure',
    name: 'Cooling System Failure',
    description: 'Cooling failure — overheating servers pulled offline for emergency repair.',
    effect: () => ({}),
    probability: 0.04,
    duration: 60,
  },
  {
    id: 'networkOutage',
    name: 'Network Outage',
    description: 'ISP upstream failure — all client traffic unreachable until restored.',
    effect: () => ({}), // handled via connectivityMultiplier = 0 in stats useMemo
    probability: 0.03,
    duration: 60,
  },
  {
    id: 'fiberCut',
    name: 'Fiber Cut',
    description: 'A contractor severed a backbone fiber — connectivity severely degraded.',
    effect: () => ({}), // handled via connectivityMultiplier × 0.3 in stats useMemo
    probability: 0.02,
    duration: 120,
  },
  {
    id: 'ddosAttack',
    name: 'DDoS Attack',
    description: 'Volumetric DDoS attack flooding your transit. Without NaWas scrubbing, connectivity drops to near-zero.',
    effect: () => ({}), // handled via connectivityMultiplier override in stats useMemo
    probability: 0.03,
    duration: 180,
  },
  {
    id: 'heatwave',
    name: 'Heatwave',
    description: 'Extreme temperatures straining your cooling — servers throttle to avoid overheating.',
    effect: () => ({}),
    probability: 0.02,
    duration: 180,
    utilizationDelta: -0.2,
  },
  {
    id: 'hardwareRecall',
    name: 'Hardware Recall',
    description: 'Vendor safety recall — a quarter of your contracted servers pulled offline for repair.',
    effect: () => ({}),
    probability: 0.02,
    duration: 90,
  },
  // ── Positive / Neutral ───────────────────────────────────────────────────────
  {
    id: 'torFailure',
    name: 'TOR Switch Failure',
    description: 'A top-of-rack switch failed. Racks protected by an MLAG redundant TOR stay online; unprotected racks go dark until the switch is swapped.',
    effect: () => ({}), // impact via forced server failures on the affected rack
    probability: 0.025,
    duration: 120,
  },
  {
    id: 'trafficSpike',
    name: 'Traffic Spike',
    description: 'A client\'s content went viral — server utilization and traffic surge. Make sure your bandwidth can handle it.',
    effect: () => ({}),
    probability: 0.04,
    duration: 180,
    positive: true,
    utilizationDelta: 0.6,
  },
  {
    id: 'viralContent',
    name: 'Viral Press Coverage',
    description: 'A major tech outlet featured your datacenter. Reputation boost — easier to win future contracts.',
    effect: () => ({}),
    probability: 0.03,
    duration: 1, // fires once, reputation change is permanent (decays naturally)
    positive: true,
  },
];

interface FacilitySafetyLite {
  coolingFailureReduction: number;
  hardwareRecallReduction: number;
}

interface UseRandomEventsProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  gameTime: number;
  isPaused: boolean;
  upgrades: { disasterPreparedness: number; [key: string]: number };
  securityReduction?: number;
  facilitySafetyByLocation?: Record<string, FacilitySafetyLite>;
  setReputation: React.Dispatch<React.SetStateAction<number>>;
  triggerNotification: (message: string, type: GameNotification['type'], duration?: number) => void;
}

/** Reputation delta per event */
const EVENT_REPUTATION_DELTA: Record<string, number> = {
  powerOutage:    -5,
  coolingFailure: -3,
  networkOutage:  -3,
  heatwave:       -2,
  hardwareRecall: -4,
  fiberCut:       -2,
  ddosAttack:     -4,
  trafficSpike:    0,
  viralContent:   +8,
};

const SECURITY_MITIGATED_EVENTS = new Set(['ddosAttack', 'hardwareRecall', 'fiberCut']);

interface RandomEventsHook {
  activeEvents: ActiveEvent[];
  setActiveEvents: Dispatch<SetStateAction<ActiveEvent[]>>;
  checkForRandomEvents: () => void;
  processActiveEvents: () => { revenueModifier: number };
  clearEvent: (eventId: string) => void;
  tickUtilization: (gameTime: number) => void;
}

function applyUtilizationDelta(
  locations: Record<string, LocationData>,
  locationId: string,
  delta: number,
  clampMin = 0.3,
  clampMax = 2.5
): Record<string, LocationData> {
  const loc = locations[locationId];
  if (!loc) return locations;
  return {
    ...locations,
    [locationId]: {
      ...loc,
      floors: Object.fromEntries(
        Object.entries(loc.floors).map(([fid, floor]) => [
          fid,
          {
            ...floor,
            racks: floor.racks.map(rack => ({
              ...rack,
              servers: rack.servers.map(server => ({
                ...server,
                utilizationRate: Math.min(clampMax, Math.max(clampMin, (server.utilizationRate ?? 1.0) + delta)),
              })),
            })),
          },
        ])
      ),
    },
  };
}

// Mark a specific list of server IDs as failed at a location
function failServers(
  locations: Record<string, LocationData>,
  locationId: string,
  serverIds: string[]
): Record<string, LocationData> {
  const loc = locations[locationId];
  if (!loc) return locations;
  return {
    ...locations,
    [locationId]: {
      ...loc,
      floors: Object.fromEntries(
        Object.entries(loc.floors).map(([fid, floor]) => [
          fid,
          {
            ...floor,
            racks: floor.racks.map(rack => ({
              ...rack,
              servers: rack.servers.map(server =>
                serverIds.includes(server.id)
                  ? { ...server, failureStatus: 'failed' as const }
                  : server
              ),
            })),
          },
        ])
      ),
    },
  };
}

// Restore servers that were force-failed by an event back to operational
function restoreServers(
  locations: Record<string, LocationData>,
  locationId: string,
  serverIds: string[]
): Record<string, LocationData> {
  const loc = locations[locationId];
  if (!loc) return locations;
  return {
    ...locations,
    [locationId]: {
      ...loc,
      floors: Object.fromEntries(
        Object.entries(loc.floors).map(([fid, floor]) => [
          fid,
          {
            ...floor,
            racks: floor.racks.map(rack => ({
              ...rack,
              servers: rack.servers.map(server =>
                serverIds.includes(server.id) && server.failureStatus === 'failed'
                  ? { ...server, failureStatus: undefined }
                  : server
              ),
            })),
          },
        ])
      ),
    },
  };
}

export const useRandomEvents = ({
  locations,
  setLocations,
  gameTime,
  isPaused,
  upgrades,
  securityReduction = 0,
  facilitySafetyByLocation = {},
  setReputation,
  triggerNotification
}: UseRandomEventsProps): RandomEventsHook => {
  const [activeEvents, setActiveEvents] = useState<ActiveEvent[]>([]);

  const checkForRandomEvents = useCallback(() => {
    if (isPaused) return;

    const disasterReduction = (upgrades.disasterPreparedness || 0) * 0.2;

    Object.entries(locations).forEach(([locationId, location]) => {
      if (!location.unlocked) return;

      // Only fire events at locations with servers installed
      const hasServers = Object.values(location.floors ?? {}).some(
        f => f.racks.some(r => r.servers.length > 0)
      );
      if (!hasServers) return;

      const facility = facilitySafetyByLocation[locationId];

      RANDOM_EVENTS.forEach(event => {
        let adjustedProbability = event.positive
          ? event.probability
          : Math.max(0, event.probability * (1 - disasterReduction));

        if (!event.positive && SECURITY_MITIGATED_EVENTS.has(event.id)) {
          adjustedProbability = Math.max(0, adjustedProbability * (1 - securityReduction));
        }

        // Facility safety equipment (Site Layout Editor) mitigates specific events
        if (facility) {
          if (event.id === 'coolingFailure') {
            adjustedProbability *= (1 - facility.coolingFailureReduction);
          } else if (event.id === 'hardwareRecall') {
            adjustedProbability *= (1 - facility.hardwareRecallReduction);
          }
        }

        if (Math.random() >= adjustedProbability) return;

        const isEventActive = activeEvents.some(
          ae => ae.id === event.id && ae.locationId === locationId
        );
        if (isEventActive) return;

        // ── Compute affected servers before updating state ──────────────────
        let affectedServerIds: string[] = [];

        if (event.id === 'powerOutage') {
          // All currently operational servers go offline
          Object.values(location.floors ?? {}).forEach(floor => {
            floor.racks.forEach(rack => {
              rack.servers.forEach(server => {
                if (!server.failureStatus || server.failureStatus === 'operational') {
                  affectedServerIds.push(server.id);
                }
              });
            });
          });
          setLocations(prev => failServers(prev, locationId, affectedServerIds));

        } else if (event.id === 'coolingFailure') {
          // ~35% of contracted operational servers fail → sent to repair
          const contracted: string[] = [];
          Object.values(location.floors ?? {}).forEach(floor => {
            floor.racks.forEach(rack => {
              rack.servers.forEach(server => {
                if (server.contractId && (!server.failureStatus || server.failureStatus === 'operational')) {
                  contracted.push(server.id);
                }
              });
            });
          });
          affectedServerIds = contracted
            .sort(() => Math.random() - 0.5)
            .slice(0, Math.max(1, Math.ceil(contracted.length * 0.35)));
          const repairTime = gameTime + 360 + Math.floor(Math.random() * 180); // 1–1.5 in-game hours
          setLocations(prev => {
            const loc = prev[locationId];
            if (!loc) return prev;
            return {
              ...prev,
              [locationId]: {
                ...loc,
                floors: Object.fromEntries(
                  Object.entries(loc.floors).map(([fid, floor]) => [
                    fid,
                    {
                      ...floor,
                      racks: floor.racks.map(rack => ({
                        ...rack,
                        servers: rack.servers.map(server =>
                          affectedServerIds.includes(server.id)
                            ? { ...server, failureStatus: 'repairing' as const, repairCompletesAt: repairTime }
                            : server
                        ),
                      })),
                    },
                  ])
                ),
              },
            };
          });

        } else if (event.id === 'hardwareRecall') {
          // 25% of contracted operational servers go to repair
          const contracted: string[] = [];
          Object.values(location.floors ?? {}).forEach(floor => {
            floor.racks.forEach(rack => {
              rack.servers.forEach(server => {
                if (server.contractId && (!server.failureStatus || server.failureStatus === 'operational')) {
                  contracted.push(server.id);
                }
              });
            });
          });
          affectedServerIds = contracted
            .sort(() => Math.random() - 0.5)
            .slice(0, Math.max(1, Math.ceil(contracted.length * 0.25)));
          const repairTime = gameTime + 720 + Math.floor(Math.random() * 360); // 2–3 in-game hours
          setLocations(prev => {
            const loc = prev[locationId];
            if (!loc) return prev;
            return {
              ...prev,
              [locationId]: {
                ...loc,
                floors: Object.fromEntries(
                  Object.entries(loc.floors).map(([fid, floor]) => [
                    fid,
                    {
                      ...floor,
                      racks: floor.racks.map(rack => ({
                        ...rack,
                        servers: rack.servers.map(server =>
                          affectedServerIds.includes(server.id)
                            ? { ...server, failureStatus: 'repairing' as const, repairCompletesAt: repairTime }
                            : server
                        ),
                      })),
                    },
                  ])
                ),
              },
            };
          });
        } else if (event.id === 'torFailure') {
          // Pick a random rack that has a primary TOR and at least one operational server.
          type Cand = { rackServerIds: string[]; protectedByMlag: boolean };
          const candidates: Cand[] = [];
          Object.values(location.floors ?? {}).forEach(floor => {
            floor.racks.forEach(rack => {
              if (!rack.torSwitch) return;
              const operational = rack.servers
                .filter(s => !s.failureStatus || s.failureStatus === 'operational')
                .map(s => s.id);
              if (operational.length === 0) return;
              candidates.push({ rackServerIds: operational, protectedByMlag: !!rack.torSwitchRedundant });
            });
          });
          // No cabled rack with live servers → nothing to fail, skip this event entirely.
          if (candidates.length === 0) return;
          const target = candidates[Math.floor(Math.random() * candidates.length)];
          if (!target.protectedByMlag) {
            // Unprotected rack goes dark until the switch is swapped.
            affectedServerIds = target.rackServerIds;
            setLocations(prev => failServers(prev, locationId, affectedServerIds));
            setReputation(prev => Math.min(110, Math.max(0, prev - 3)));
          }
          // Protected racks: MLAG carries the traffic → affectedServerIds stays empty.
        }

        // ── Apply utilization delta for events that have one ────────────────
        if (event.utilizationDelta !== undefined) {
          setLocations(prev => applyUtilizationDelta(prev, locationId, event.utilizationDelta!));
        }

        // ── Reputation impact ───────────────────────────────────────────────
        const repDelta = EVENT_REPUTATION_DELTA[event.id] ?? 0;
        if (repDelta !== 0) {
          setReputation(prev => Math.min(110, Math.max(0, prev + repDelta)));
        }

        // ── Notifications ───────────────────────────────────────────────────
        const locName = location.name;
        const notifType = event.positive ? 'success' : 'warning';

        if (event.id === 'powerOutage') {
          triggerNotification(
            `Power Outage at ${locName} — ${affectedServerIds.length} servers offline! SLA breaches triggered.`,
            'error', 12000
          );
        } else if (event.id === 'coolingFailure') {
          triggerNotification(
            `Cooling failure at ${locName} — ${affectedServerIds.length} servers overheating, sent for repair.`,
            'error', 12000
          );
        } else if (event.id === 'networkOutage') {
          triggerNotification(
            `Network Outage at ${locName} — ISP upstream failure, all clients unreachable.`,
            'warning', 12000
          );
        } else if (event.id === 'fiberCut') {
          triggerNotification(
            `Fiber Cut near ${locName} — connectivity down to 30%. SLA breach triggered.`,
            'warning', 12000
          );
        } else if (event.id === 'ddosAttack') {
          const nawas = location.nawasSubscription;
          if (nawas) {
            const tier = NAWAS_TIERS.find(t => t.id === nawas.tierId);
            triggerNotification(
              `DDoS attack at ${locName} — NaWas ${tier?.name} scrubbing active. Impact minimal.`,
              'info', 8000
            );
          } else {
            triggerNotification(
              `DDoS attack at ${locName}! Transit flooded — connectivity near zero. Subscribe to NaWas to protect.`,
              'error', 12000
            );
          }
        } else if (event.id === 'heatwave') {
          triggerNotification(
            `Heatwave at ${locName} — servers throttling to protect cooling. Bandwidth saturation risk.`,
            'warning', 10000
          );
        } else if (event.id === 'hardwareRecall') {
          triggerNotification(
            `Hardware Recall at ${locName} — ${affectedServerIds.length} servers offline for mandatory safety inspection.`,
            'error', 12000
          );
        } else if (event.id === 'torFailure') {
          if (affectedServerIds.length > 0) {
            triggerNotification(
              `TOR switch failed at ${locName} — ${affectedServerIds.length} servers offline (no MLAG redundancy). Install a redundant TOR to survive this.`,
              'error', 12000
            );
          } else {
            triggerNotification(
              `TOR switch failed at ${locName} — MLAG redundant switch carried the traffic. No downtime.`,
              'info', 8000
            );
          }
        } else if (event.id === 'trafficSpike') {
          triggerNotification(
            `Traffic Spike at ${locName} — utilization surging. Check bandwidth headroom.`,
            'info', 8000
          );
        } else if (event.id === 'viralContent') {
          triggerNotification(
            `Press Coverage — your datacenter was featured in a major tech outlet! Reputation +8.`,
            'success', 10000
          );
        } else {
          triggerNotification(`${event.name} at ${locName}: ${event.description}`, notifType, 10000);
        }

        const newEvent: ActiveEvent = {
          ...event,
          locationId,
          remainingDuration: event.duration,
          startTime: gameTime,
          affectedServerIds,
        };
        setActiveEvents(prev => [...prev, newEvent]);
      });
    });
  }, [locations, setLocations, isPaused, activeEvents, gameTime, upgrades.disasterPreparedness, securityReduction, facilitySafetyByLocation, setReputation, triggerNotification]);

  const processActiveEvents = useCallback(() => {
    if (isPaused || activeEvents.length === 0) {
      return { revenueModifier: 0 };
    }

    setActiveEvents(prev => {
      const updated = prev.map(event => ({
        ...event,
        remainingDuration: event.remainingDuration - 1,
      }));

      updated.forEach(event => {
        if (event.remainingDuration > 0) return;

        const location = locations[event.locationId];
        const locName = location?.name ?? event.locationId;

        if (event.id === 'networkOutage') {
          triggerNotification(`Network restored at ${locName} — ISP upstream is back online. Client traffic resuming.`, 'success', 6000);
        } else if (event.id === 'fiberCut') {
          triggerNotification(`Fiber repaired at ${locName} — full connectivity restored.`, 'success', 6000);
        } else if (event.id === 'coolingFailure') {
          triggerNotification(`Cooling systems back online at ${locName} — affected servers returning to service.`, 'success', 6000);
        } else if (event.id === 'hardwareRecall') {
          triggerNotification(`Hardware recall resolved at ${locName} — inspected servers cleared and back online.`, 'success', 6000);
        } else if (event.id === 'heatwave') {
          triggerNotification(`Heatwave passed at ${locName} — temperatures normalising, server throttling lifted.`, 'info', 5000);
        } else if (event.id === 'ddosAttack') {
          triggerNotification(`DDoS attack on ${locName} subsided — traffic returning to normal.`, 'info', 5000);
        } else if (event.id === 'torFailure') {
          if ((event.affectedServerIds?.length ?? 0) > 0) {
            triggerNotification(`TOR switch replaced at ${locName} — rack back online.`, 'success', 6000);
          }
        } else if (event.id === 'trafficSpike') {
          triggerNotification(`Traffic spike at ${locName} has stabilised.`, 'info', 4000);
        } else {
          triggerNotification(`${event.name} at ${locName} has ended.`, 'info', 5000);
        }

        // Revert utilization delta
        if (event.utilizationDelta !== undefined && event.id !== 'ddosAttack') {
          setLocations(prev => applyUtilizationDelta(prev, event.locationId, -event.utilizationDelta!));
        }

        // Restore servers that were force-failed by a power outage
        if (event.id === 'powerOutage' && (event.affectedServerIds?.length ?? 0) > 0) {
          setLocations(prev => restoreServers(prev, event.locationId, event.affectedServerIds!));
          triggerNotification(`Power restored at ${locName} — servers coming back online.`, 'success', 6000);
        }

        // Restore servers that went dark on an unprotected TOR failure
        if (event.id === 'torFailure' && (event.affectedServerIds?.length ?? 0) > 0) {
          setLocations(prev => restoreServers(prev, event.locationId, event.affectedServerIds!));
        }
      });

      return updated.filter(e => e.remainingDuration > 0);
    });

    return { revenueModifier: 0 };
  }, [activeEvents, locations, setLocations, isPaused, triggerNotification]);

  const clearEvent = useCallback((eventId: string) => {
    setActiveEvents(prev => prev.filter(e => e.id !== eventId));
  }, []);

  // Random walk server utilization — only contracted, non-failed servers
  const tickUtilization = useCallback((_gameTime: number) => {
    setLocations(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(locId => {
        const loc = next[locId];
        if (!loc.unlocked) return;
        next[locId] = {
          ...loc,
          floors: Object.fromEntries(
            Object.entries(loc.floors).map(([fid, floor]) => [
              fid,
              {
                ...floor,
                racks: floor.racks.map(rack => ({
                  ...rack,
                  servers: rack.servers.map(server => {
                    if (!server.contractId || server.failureStatus === 'failed')
                      return { ...server, utilizationRate: 0 };
                    const current = server.utilizationRate ?? 1.0;
                    // Only protect event-driven spikes above ceiling from normal drift
                    if (current > 1.15) return server;
                    // Servers freshly assigned a contract (or stuck at 0) ramp up toward 0.75
                    if (current < 0.5) {
                      const ramp = Math.min(0.75, current + 0.05 + Math.random() * 0.10);
                      return { ...server, utilizationRate: ramp };
                    }
                    const drift = (Math.random() - 0.5) * 0.10;
                    const next = Math.min(1.15, Math.max(0.5, current + drift));
                    return { ...server, utilizationRate: next };
                  }),
                })),
              },
            ])
          ),
        };
      });
      return next;
    });
  }, [setLocations]);

  return {
    activeEvents,
    setActiveEvents,
    checkForRandomEvents,
    processActiveEvents,
    clearEvent,
    tickUtilization,
  };
};
